<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import Skeleton from 'primevue/skeleton';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const toast = useToast();


</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-if="authStore.isSkeletonPopupVisible"
        class="absolute z-10 w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center items-start md:items-center px-4 md:px-8 py-6 md:py-0">
        <Transition name="modal-inner" class="rounded-2xl">
          <div
            class="custom-skeleton fixed top-10 z-50 rounded-2xl border border-gray-100 bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-xl p-6 md:p-8 w-10/12 max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="flex items-center justify-between mb-8">
              <div>
                <Skeleton width="20rem" height="2rem" class="mb-2"></Skeleton>
                <Skeleton width="30rem" height="1rem" class="mt-1"></Skeleton>
              </div>
            </div>

            <!-- Top info section -->
            <section class="bg-[#f4f5f7] border-gray-100 rounded-3xl px-6 py-8 shadow-sm">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="flex flex-col gap-2">
                  <Skeleton width="4rem" height="1rem"></Skeleton>
                  <Skeleton height="2rem" class="rounded-xl"></Skeleton>
                </div>
                <div class="flex flex-col gap-2">
                  <Skeleton width="8rem" height="1rem"></Skeleton>
                  <Skeleton height="2rem" class="rounded-xl"></Skeleton>
                </div>
                <div class="flex flex-col gap-2">
                  <Skeleton width="6rem" height="1rem"></Skeleton>
                  <Skeleton height="2rem" class="rounded-xl"></Skeleton>
                </div>
                <div class="flex flex-col gap-2">
                  <Skeleton width="10rem" height="1rem"></Skeleton>
                  <Skeleton height="2rem" class="rounded-xl"></Skeleton>
                </div>
              </div>
            </section>

            <!-- Bills / Coins Section -->
            <div class="relative border rounded-3xl mt-8 px-8 py-9 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section>
                <!-- <Skeleton shape="rectangle" width="5rem" height="2.5rem" class="absolute right-8 top-8 rounded-xl">
                </Skeleton> -->

                <Skeleton width="4rem" height="1.5rem" class="mb-4"></Skeleton>

                <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 max-h-[450px] overflow-y-auto pr-2">
                  <div v-for="i in 5" :key="i" class="rounded-2xl border border-gray-100 bg-gray-100 shadow-sm p-4">
                    <div class="flex items-start justify-between mb-1">
                      <Skeleton width="4rem" height="1.2rem"></Skeleton>
                    </div>
                    <div class="flex items-center justify-between">
                      <Skeleton width="6rem" height="2.5rem" class="rounded-xl"></Skeleton>

                      <div class="text-right">
                        <Skeleton width="5rem" height="0.7rem" class="mb-1"></Skeleton>
                        <Skeleton width="6rem" height="1.2rem"></Skeleton>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <Skeleton width="5rem" height="1.5rem" class="mb-4"></Skeleton>

                <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 max-h-[450px] overflow-y-auto pr-2">
                  <div v-for="i in 6" :key="i" class="rounded-2xl border border-gray-100 bg-gray-100 shadow-sm p-4">
                    <div class="flex items-start justify-between mb-1">
                      <Skeleton width="4rem" height="1.2rem"></Skeleton>
                    </div>
                    <div class="flex items-center justify-between">
                      <Skeleton width="6rem" height="2.5rem" class="rounded-xl"></Skeleton>

                      <div class="text-right">
                        <Skeleton width="5rem" height="0.7rem" class="mb-1"></Skeleton>
                        <Skeleton width="6rem" height="1.2rem"></Skeleton>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <!-- Summary Section -->
            <div class="bg-gray-100 p-8 rounded-3xl grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
              <div class="rounded-2xl border border-gray-100 bg-white/80 backdrop-blur px-6 py-6 shadow-sm">
                <Skeleton width="6rem" height="1rem" class="mb-2"></Skeleton>
                <Skeleton width="5rem" height="2rem" class="mt-1"></Skeleton>
              </div>
              <div class="rounded-2xl border border-gray-100 bg-white/80 backdrop-blur px-6 py-6 shadow-sm">
                <Skeleton width="7rem" height="1rem" class="mb-2"></Skeleton>
                <Skeleton width="5rem" height="2rem" class="mt-1"></Skeleton>
              </div>
              <div class="rounded-2xl border border-gray-200 bg-white px-6 py-6 shadow-md">
                <Skeleton width="10rem" height="1rem" class="mb-2"></Skeleton>
                <Skeleton width="5rem" height="2rem" class="mt-1"></Skeleton>
              </div>
              <div class="flex justify-end items-end">
                <Skeleton
                  width="6rem"
                  height="2.5rem"
                  class="rounded-xl"
                />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

.custom-skeleton {
  --p-skeleton-background: theme('colors.slate.200');
  --p-skeleton-animation-background: theme('colors.slate.100');
  --p-skeleton-border-radius: theme('borderRadius.md');
}

.dark .custom-skeleton {
  --p-skeleton-background: theme('colors.gray.100');
  --p-skeleton-animation-background: theme('colors.gray.100');
}
</style>